/* eslint-disable new-parens */
import React from "react";
import { ChevronRight } from "lucide-react";
import { BULANDI_CONCLUDED, bulandi2026Meta } from "../../../data/bulandi2026Data";

export function BulandiHeroLiveBanner({ className = "" }) {
  return React.createElement(
    "div",
    {
      className: `flex w-full flex-col items-center ${className}`.trim(),
      role: "status",
    },
    React.createElement(
      "p",
      {
        className: BULANDI_CONCLUDED
          ? "relative flex items-center justify-center gap-2 whitespace-nowrap font-display text-[1.40625rem] uppercase leading-none tracking-[0.08em] text-bul-fg sm:text-[1.6875rem] md:text-[2.109375rem] lg:text-[2.53125rem] xl:text-[2.8125rem]"
          : "bulandi-hero-live-banner relative flex items-center justify-center gap-2 whitespace-nowrap font-display text-[1.40625rem] uppercase leading-none tracking-[0.08em] sm:text-[1.6875rem] md:text-[2.109375rem] lg:text-[2.53125rem] xl:text-[2.8125rem]",
      },
      BULANDI_CONCLUDED
        ? null
        : React.createElement("span", {
            className: "bulandi-hero-live-dot",
            "aria-hidden": true,
          }),
      React.createElement(
        "span",
        {
          className: BULANDI_CONCLUDED
            ? "bul-text-gradient-fire"
            : "bulandi-hero-live-text",
        },
        BULANDI_CONCLUDED ? "That\u2019s a wrap" : "Bulandi is LIVE",
      ),
    ),
    BULANDI_CONCLUDED
      ? React.createElement(
          "p",
          {
            className:
              "mt-3 max-w-md text-center font-mono text-xs font-bold uppercase tracking-[0.2em] text-bul-mutedfg sm:text-sm",
          },
          "Bulandi 2026 has concluded",
        )
      : null,
  );
}

/**
 * Archive notice used wherever a Bulandi registration form used to be.
 * @param {{ title?: string; body?: string; className?: string }} props
 */
export function BulandiClosedNotice({ title, body, className = "" }) {
  return React.createElement(
    "div",
    {
      className:
        `rounded-2xl border-2 border-bul-border bg-bul-surface/90 p-6 text-center shadow-bul-brutal-sm sm:p-8 ${className}`.trim(),
      role: "status",
    },
    React.createElement(
      "p",
      {
        className:
          "font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime sm:text-xs",
      },
      bulandi2026Meta.eventConcludedLabel || "Registrations closed",
    ),
    React.createElement(
      "h3",
      {
        className:
          "mt-3 font-display text-[1.125rem] uppercase leading-tight tracking-tight text-bul-fg sm:text-[1.40625rem]",
      },
      React.createElement(
        "span",
        { className: "bul-heading-chrome" },
        title || "Bulandi 2026 has ",
      ),
      React.createElement("span", { className: "bul-text-gradient-fire" }, "concluded"),
    ),
    React.createElement(
      "p",
      {
        className:
          "mx-auto mt-4 max-w-xl text-sm leading-relaxed text-bul-mutedfg sm:text-base",
      },
      body ||
        "Registrations and event sign-in are now closed. This page stays up as an archive \u2014 browse the event catalogue, winners, workshops and our sponsors.",
    ),
  );
}

/** Events catalogue tab CTA \u2014 registers while open, shows a closed chip once concluded. */
export function BulandiEventsRegisterCta({ onRegister }) {
  if (BULANDI_CONCLUDED) {
    return React.createElement(
      "span",
      {
        className:
          "flex min-h-[52px] shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-bul-border bg-bul-bg/60 px-6 py-3 text-center font-mono text-xs font-bold uppercase tracking-wide text-bul-mutedfg sm:min-h-[56px] sm:px-8 sm:text-sm",
        role: "status",
      },
      bulandi2026Meta.eventConcludedLabel || "Registrations closed",
    );
  }
  return React.createElement(
    PrimaryCta,
    {
      as: "button",
      type: "button",
      onClick: onRegister,
      className:
        "bul-cta-lg min-h-[52px] shrink-0 px-6 text-sm sm:min-h-[56px] sm:px-8 sm:text-base",
    },
    "Register",
    React.createElement(ChevronRight, {
      className: "h-5 w-5",
      "aria-hidden": true,
      strokeWidth: 3,
    }),
  );
}

export function CountdownStat({ value, label, compact = false }) {
  if (compact) {
    return React.createElement(
      "div",
      {
        className:
          "flex shrink-0 flex-col items-center min-w-[2.5rem] sm:min-w-[3rem] rounded-lg border-2 border-bul-border bg-bul-surface/80 px-1.5 py-1.5 sm:px-2 sm:py-2 shadow-bul-brutal-sm",
      },
      React.createElement(
        "span",
        {
          className:
            "font-display text-[0.84375rem] sm:text-[0.9375rem] tabular-nums bul-text-gradient-hero leading-none",
        },
        String(value).padStart(2, "0"),
      ),
      React.createElement(
        "span",
        {
          className:
            "mt-0.5 font-mono text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-bul-mutedfg",
        },
        label,
      ),
    );
  }
  return React.createElement(
    "div",
    {
      className:
        "flex flex-col items-center min-w-[64px] sm:min-w-[88px] rounded-xl border-2 border-bul-border bg-bul-surface/80 px-3 py-3 sm:px-5 sm:py-4 shadow-bul-brutal",
    },
    React.createElement(
      "span",
      {
        className:
          "font-display text-[1.40625rem] sm:text-[2.25rem] tabular-nums bul-text-gradient-hero leading-none",
      },
      String(value).padStart(2, "0"),
    ),
    React.createElement(
      "span",
      {
        className:
          "mt-1 font-mono text-[10px] sm:text-xs font-bold uppercase tracking-widest text-bul-mutedfg",
      },
      label,
    ),
  );
}

export function PrimaryCta({ as = "button", children, className = "", ...props }) {
  const Comp = as;
  return React.createElement(
    Comp,
    Object.assign({}, props, { className: `bul-cta-primary ${className}` }),
    children,
  );
}

export function GhostCta({ as = "button", children, className = "", ...props }) {
  const Comp = as;
  return React.createElement(
    Comp,
    Object.assign({}, props, { className: `bul-cta-ghost ${className}` }),
    children,
  );
}

export function SectionHeader({
  eyebrow,
  title,
  accent,
  description,
  descriptionClassName,
}) {
  const descCls = [
    "mt-3 sm:mt-4 max-w-2xl text-sm sm:text-base text-bul-mutedfg leading-relaxed mx-auto sm:mx-0",
    descriptionClassName,
  ]
    .filter(Boolean)
    .join(" ");
  return React.createElement(
    "header",
    { className: "mb-8 lg:mb-10 text-center sm:text-left" },
    eyebrow
      ? React.createElement(
          "p",
          {
            className:
              "font-mono text-xs font-bold uppercase tracking-[0.3em] text-bul-lime mb-3",
          },
          eyebrow,
        )
      : null,
    React.createElement(
      "h2",
      {
        className:
          "font-display text-[1.40625rem] sm:text-[1.6875rem] lg:text-[2.25rem] uppercase tracking-tight text-bul-fg",
      },
      React.createElement("span", { className: "bul-heading-chrome" }, title),
      " ",
      accent
        ? React.createElement("span", { className: "bul-text-gradient-fire" }, accent)
        : null,
    ),
    description ? React.createElement("p", { className: descCls }, description) : null,
  );
}
